import * as React from 'react';
import { inject, observer } from 'mobx-react';

import { RouteStore, AnalyticsStore } from 'bernie-core';
import { Viewport, ViewSmall, ViewLarge } from 'bernie-view';
import { UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkRadioGroup, UitkRadio } from 'uitk-react-switch';

import { SearchFormOptionsDialog, SearchFormOptionsList, SearchFormOptionsDialogWrapper, WizardMenu } from '.';
import { SearchFormStore } from '../../../../stores';
import { trackAnalytics } from 'utility';
import { FlightsSearchFormAirlineOption, FlightsSearchFormAirlineOptionItem } from 'types/search/wizard';
import { InteractableFieldComponentProps } from 'types/shared';

interface AirlinesOptionsProps extends InteractableFieldComponentProps {
  routeStore?: RouteStore;
  searchOption: FlightsSearchFormAirlineOption;
  analytics?: AnalyticsStore;
  searchFormStore?: SearchFormStore;
}

@inject('routeStore', 'analytics', 'searchFormStore')
@observer
export class AirlinesOptions extends React.Component<AirlinesOptionsProps> {
  public state = { isOpen: false };
  private initialAirline: string;
  private shouldSaveChanges: boolean;

  public handleRadioGroupOnChange(searchOption: FlightsSearchFormAirlineOption, analytics: AnalyticsStore, input) {
    const inputValue: string = input.target.value;
    this.updateAirlinePreference(searchOption, inputValue, analytics);
  }

  public updateAirlinePreference(
    searchOption: FlightsSearchFormAirlineOption,
    selectedAirline: string,
    analytics: AnalyticsStore,
    checkAnalytics = true
  ) {
    const { items } = searchOption;
    const previousAirline = items.find(item => item.selected);

    items.forEach(item => {
      item.selected = selectedAirline === item.airline;
    });

    const selectedAirlineItem = items.find(item => item.selected);

    searchOption.selectedItem = selectedAirlineItem;
    if (checkAnalytics && previousAirline.airline !== selectedAirlineItem.airline) {
      trackAnalytics(selectedAirlineItem.airlineSelectedAnalytics, analytics);
    }
  }

  public menuListItemMapper(searchOption: FlightsSearchFormAirlineOption, analytics: AnalyticsStore) {
    const updateFnRef = this.updateAirlinePreference;

    return searchOption.items.map((item: FlightsSearchFormAirlineOptionItem) => ({
      attributes: {
        role: 'menuItem',
        tabindex: 0
      },
      className: 'uitk-list-item',
      onClick() {
        updateFnRef(searchOption, item.airline, analytics);
      },
      label: item.label,
      typeAnchor: false
    }));
  }

  public openCallback = () => {
    const { searchOption, analytics, onFieldInteraction } = this.props;
    const { openAnalytics } = searchOption;
    onFieldInteraction && onFieldInteraction();
    trackAnalytics(openAnalytics, analytics);
    this.initialAirline = searchOption.selectedItem.airline;
    this.shouldSaveChanges = false;
  };

  public dialogUnmountCallback = () => {
    const { searchOption, analytics } = this.props;

    if (!this.shouldSaveChanges) {
      const { cancelAnalytics } = searchOption;
      trackAnalytics(cancelAnalytics, analytics);
      this.updateAirlinePreference(searchOption, this.initialAirline, analytics, false);
    }
  };

  private closeNestedDialog = () => {
    const { routeStore } = this.props;

    routeStore.closeDialog('pwaDialogNested');
  };

  private submitChanges = () => {
    const { searchOption, analytics } = this.props;
    const { applyAnalytics } = searchOption;

    trackAnalytics(applyAnalytics, analytics);
    this.shouldSaveChanges = true;
    this.closeNestedDialog();
  };

  public toggleMenu = () => {
    const { searchOption, analytics, onFieldInteraction } = this.props;
    const { openAnalytics, cancelAnalytics } = searchOption;
    const isOpen = !this.state.isOpen;
    onFieldInteraction && onFieldInteraction();
    isOpen ? trackAnalytics(openAnalytics, analytics) : trackAnalytics(cancelAnalytics, analytics);

    return this.setState({ isOpen });
  };

  public render() {
    const { searchOption, searchFormStore, analytics } = this.props;
    const { __typename, doneAction, fieldIcon, icon, items, label, doneActionAccessibility, closeIcon } = searchOption;
    const dialogId = `${__typename}-dialog`;
    const selectedAirline = searchOption.selectedItem.airline;
    const loading = searchFormStore.searchForm.__typename === 'FlightsLoadingSearchForm';
    const triggerLabel = loading
      ? searchOption.selectedItem.label
      : items.find(item => item.airline === selectedAirline).label;

    const radioGroupOnChange: () => void = this.handleRadioGroupOnChange.bind(
      this,
      searchOption,
      analytics
    ) as () => void;

    return (
      <Viewport>
        <ViewSmall>
          {!loading && (
            <UitkLayoutGridItem colSpan={{ small: 2, medium: 1 }}>
              <div>
                <SearchFormOptionsDialogWrapper
                  dialogId={dialogId}
                  label={triggerLabel}
                  labelIcon={fieldIcon && fieldIcon.id}
                  subLabel={label}
                >
                  <SearchFormOptionsDialog
                    key={dialogId}
                    unmountCallback={this.dialogUnmountCallback}
                    dialogId={dialogId}
                    dismissDialogAction={this.closeNestedDialog}
                    doneAction={doneAction}
                    doneButtonClick={this.submitChanges}
                    openCallback={this.openCallback}
                    doneActionAccessibility={doneActionAccessibility}
                    cancelActionAccessibility={closeIcon && closeIcon.accessibility}
                  >
                    <div key={`${dialogId}-content`} className="all-x-margin-three all-t-margin-three all-b-margin-six">
                      <p className="uitk-type-heading-400 all-b-margin-two" key={`${dialogId}-content-label`}>
                        {label}
                      </p>
                      <UitkRadioGroup
                        group={`${searchOption.__typename}-group`}
                        onChange={radioGroupOnChange}
                        selected={selectedAirline}
                        key={`${dialogId}-items`}
                      >
                        {items.map((item, index) => (
                          <UitkRadio
                            id={`${item.airline}`}
                            key={`${item.airline}-${index}`}
                            value={item.airline}
                            label={item.label}
                          />
                        ))}
                      </UitkRadioGroup>
                    </div>
                  </SearchFormOptionsDialog>
                </SearchFormOptionsDialogWrapper>
              </div>
            </UitkLayoutGridItem>
          )}
        </ViewSmall>
        <ViewLarge>
          <WizardMenu
            toggleMenu={this.toggleMenu}
            isOpen={this.state.isOpen}
            label={triggerLabel}
            labelIcon={icon.id}
            maxHeight={500}
            dataTestId="flights-airlines-options-toggle"
          >
            {!loading && <SearchFormOptionsList items={this.menuListItemMapper(searchOption, analytics)} />}
          </WizardMenu>
        </ViewLarge>
      </Viewport>
    );
  }
}
